import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Row } from "@/Components/ui";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ServerPaginatedTable from "../../Components/Common/ServerPaginatedTable";
import ExportCSVModal from "../../Components/Common/ExportCSVModal";
import {
  API_ORDER_ERROR_HANDLINGS_ACTIONS_CRM,
  API_ORDER_ERROR_HANDLINGS_CRM,
  API_ORDER_ERROR_HANDLINGS_STATUS_CRM,
  BACKEND_HOST,
  getDataFromUrl,
  postData,
} from "../../api";
import { SUBDOMAIN } from "../../assets/meta/meta";
import QueryString from "qs";
import moment from "moment";
import toast from "react-hot-toast";
import { utils, writeFileXLSX } from "xlsx";
import { dialogService, normalizeMessage } from "../../utils/dialogService";

const RECEIPT_SUPPORTED_PAYGATES = ["toss", "nicepay", "hecto"];

const STATUS_LABELS = {
  open: "미처리",
  resolved: "완료표시",
  all: "전체",
} as const;

type OrderErrorStatus = "open" | "resolved";
type OrderErrorAction = "recheck" | "retry";

type OrderErrorRow = {
  id: number;
  status: OrderErrorStatus;
  error_date?: string;
  order_date?: string;
  site_name?: string;
  subdomain?: string;
  order_id?: string;
  username?: string;
  phone_num?: string;
  product_name?: string;
  error_type?: string;
  pg_mid?: string;
  paygate?: string;
  payment_type?: string;
  order_state?: string;
  payment_amount?: string;
  cancel_amount?: string;
  extra_message?: string;
  error_message?: string;
};

function buildCsvRows(rows: any[]) {
  return rows.map((row) => ({
    발생일시: row.error_date ? moment(row.error_date).format("YYYY-MM-DD HH:mm:ss") : "",
    처리상태: row.status === "resolved" ? "완료표시" : "미처리",
    지점: row.site_name || row.subdomain || "",
    주문번호: row.order_id || "",
    고객명: row.username || "",
    전화번호: row.phone_num || "",
    상품명: row.product_name || "",
    오류유형: row.error_type || "",
    PG: row.pg_mid ? `${row.paygate} (${row.pg_mid})` : row.paygate || "",
    결제유형: row.payment_type || "",
    주문상태: row.order_state || "",
    결제금액: row.payment_amount || "",
    취소요청금액: row.cancel_amount || "",
    추가메모: row.extra_message || "",
    원본문구: row.error_message || "",
  }));
}

function buildTossCsvRows(rows: OrderErrorRow[]) {
  return rows
    .filter((row) => row.paygate?.toLowerCase() === "toss" && row.order_state !== "부분취소")
    .map((row) => ({
      주문번호: row.order_id || "",
      mid: row.pg_mid || "",
      사유: row.error_type || row.extra_message || row.error_message || "",
    }));
}

function formatCurrency(value: number) {
  return `${Math.max(0, Number(value) || 0).toLocaleString()}원`;
}

const OrderErrorHandlingList = () => {
  const site = (SUBDOMAIN || "").replace("crm", "");

  const [data, setData] = useState<OrderErrorRow[]>([]);
  const [previous, setPrevious] = useState("");
  const [next, setNext] = useState("");
  const [count, setCount] = useState(0);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState(0);
  const [totalCancelAmount, setTotalCancelAmount] = useState(0);
  const [query, setQuery] = useState({
    filter_site: site,
    status: "open",
  });

  const [exportRows, setExportRows] = useState<any[]>([]);
  const [tossExportRows, setTossExportRows] = useState<any[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [activeAction, setActiveAction] = useState<OrderErrorAction | null>(null);

  const pageIds = useMemo(() => data.map((row) => row.id), [data]);
  const isAllCurrentPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const selectedCount = selectedIds.length;

  const columns = useMemo(
    () => [
      {
        Header: (
          <input
            type="checkbox"
            className="form-check-input"
            aria-label="현재 페이지 전체 선택"
            checked={isAllCurrentPageSelected}
            onChange={(event) => {
              if (event.target.checked) {
                setSelectedIds(pageIds);
                return;
              }
              setSelectedIds([]);
            }}
          />
        ),
        id: "select",
        Cell: (cellProps) => {
          const rowId = cellProps.row.original.id;
          return (
            <input
              type="checkbox"
              className="form-check-input"
              aria-label={`오류 항목 ${rowId} 선택`}
              checked={selectedIds.includes(rowId)}
              disabled={isStatusUpdating || !!activeAction}
              onChange={(event) => {
                setSelectedIds((current) => {
                  if (event.target.checked) {
                    return current.includes(rowId) ? current : [...current, rowId];
                  }
                  return current.filter((id) => id !== rowId);
                });
              }}
            />
          );
        },
      },
      {
        Header: "발생일시",
        accessor: "error_date",
        id: "error_date",
        Cell: (cellProps) => (
          <div>
            <div>{cellProps.row.original.error_date ? moment(cellProps.row.original.error_date).format("YYYY-MM-DD HH:mm") : "-"}</div>
            <div className="text-muted">{cellProps.row.original.site_name || cellProps.row.original.subdomain || "-"}</div>
          </div>
        ),
      },
      {
        Header: "주문일시",
        accessor: "order_date",
        id: "order_date",
        Cell: (cellProps) => (
          <div>{cellProps.row.original.order_date ? moment(cellProps.row.original.order_date).format("YYYY-MM-DD HH:mm") : "-"}</div>
        ),
      },
      {
        Header: "상태",
        accessor: "status",
        id: "status",
        Cell: (cellProps) => {
          const status = cellProps.row.original.status === "resolved" ? "resolved" : "open";
          return (
            <Badge color={status === "resolved" ? "light" : "danger"}>
              {status === "resolved" ? "완료표시" : "미처리"}
            </Badge>
          );
        },
      },
      {
        Header: "주문",
        accessor: "order_id",
        id: "order_id",
        Cell: (cellProps) => (
          <div>
            <div className="fw-semibold">#{cellProps.row.original.order_id || "-"}</div>
            <div className="text-muted">
              {cellProps.row.original.username || "-"} ({cellProps.row.original.phone_num || "-"})
            </div>
            <div className="text-muted">{cellProps.row.original.product_name || "-"}</div>
          </div>
        ),
      },
      {
        Header: "오류",
        accessor: "error_type",
        id: "error_type",
        Cell: (cellProps) => (
          <div>
            <div>{cellProps.row.original.error_type || "-"}</div>
            <div className="text-muted" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              {cellProps.row.original.extra_message || "-"}
            </div>
          </div>
        ),
      },
      {
        Header: "금액",
        accessor: "cancel_amount",
        id: "cancel_amount",
        Cell: (cellProps) => (
          <div>
            <div>결제 {cellProps.row.original.payment_amount || "-"}</div>
            <div className="text-muted">취소 {cellProps.row.original.cancel_amount || "-"}</div>
          </div>
        ),
      },
      {
        Header: "PG",
        accessor: "paygate",
        id: "paygate",
        Cell: (cellProps) => (
          <div>
            <div>{cellProps.row.original.pg_mid ? `${cellProps.row.original.paygate} (${cellProps.row.original.pg_mid})` : (cellProps.row.original.paygate || "-")}</div>
            <div className="text-muted">{cellProps.row.original.payment_type || "-"}</div>
          </div>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        id: "",
        Cell: (cellProps) => {
          const row = cellProps.row.original;
          const canOpenReceipt = !!row.order_id && RECEIPT_SUPPORTED_PAYGATES.includes(row.paygate);
          return (
            <div className="text-end">
              <Button
                size="sm"
                color="secondary"
                disabled={!canOpenReceipt}
                onClick={() => {
                  if (!canOpenReceipt) return;
                  window.open(
                    `${BACKEND_HOST}/package/get-order-receipt/${encodeURIComponent(row.order_id)}/`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                영수증
              </Button>
            </div>
          );
        },
      },
    ],
    [activeAction, isAllCurrentPageSelected, isStatusUpdating, pageIds, selectedIds]
  );

  const requestData = (url: string, baseQuery = query) => {
    const parsedQuery = (() => {
      try {
        const urlObject = new URL(url, window.location.origin);
        return QueryString.parse(urlObject.search.replace(/^\?/, ""));
      } catch (error) {
        return baseQuery;
      }
    })();
    const effectiveQuery = { ...query, ...parsedQuery, filter_site: site };

    getDataFromUrl(url)
      .then((res) => {
        const results = res.results || [];
        setData(results);
        setSelectedIds((current) => current.filter((id) => results.some((row: OrderErrorRow) => row.id === id)));

        const page = res.page || 1;
        const pageSize = res.page_size || 15;
        const total = res.total || results.length;
        const totalPage = res.total_page || Math.ceil(total / pageSize);
        const base = API_ORDER_ERROR_HANDLINGS_CRM.replace(/\/$/, "");

        setCount(total);
        setTotalPaymentAmount(Number(res.total_payment_amount || 0));
        setTotalCancelAmount(Number(res.total_cancel_amount || 0));
        setPrevious(page > 1 ? `${base}/?${QueryString.stringify({ ...effectiveQuery, page: page - 1, page_size: pageSize })}` : "");
        setNext(page < totalPage ? `${base}/?${QueryString.stringify({ ...effectiveQuery, page: page + 1, page_size: pageSize })}` : "");
      })
      .catch(console.warn);
  };

  const refetch = (nextQuery = query) => {
    const base = API_ORDER_ERROR_HANDLINGS_CRM.replace(/\/$/, "");
    requestData(`${base}/?${QueryString.stringify({ ...nextQuery, page: 1 })}`, nextQuery);
  };

  useEffect(() => {
    refetch(query);
  }, []);

  const handleStatusChange = (status: "open" | "all" | "resolved") => {
    const nextQuery = {
      ...query,
      status,
      page: 1,
    };
    setQuery(nextQuery);
    refetch(nextQuery);
  };

  const handleExport = async () => {
    setIsExportLoading(true);
    try {
      const exportQuery = { ...query, export: 1 };
      const exportUrl = `${API_ORDER_ERROR_HANDLINGS_CRM.replace(/\/$/, "")}/?${QueryString.stringify(exportQuery)}`;
      const response = await getDataFromUrl(exportUrl);
      const rows = response.results || [];
      setExportRows(buildCsvRows(rows));
      setTossExportRows(buildTossCsvRows(rows));
      setShowExportModal(true);
    } catch (error) {
      console.warn(error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleTossExport = () => {
    const worksheet = utils.json_to_sheet(tossExportRows, {
      header: ["주문번호", "mid", "사유"],
    });
    worksheet["!cols"] = [{ wch: 38 }, { wch: 20 }, { wch: 48 }];

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "토스 양식");
    writeFileXLSX(workbook, `toss-order-error-handlings-${moment().format("YYYYMMDD-HHmmss")}.xlsx`);
  };

  const handleBulkStatusUpdate = async (status: OrderErrorStatus) => {
    if (!selectedIds.length) {
      toast.error("상태를 변경할 항목을 먼저 선택해주세요.");
      return;
    }

    const label = status === "resolved" ? "처리완료" : "미처리";
    const confirmed = await dialogService.confirm({
      title: "주문 오류 상태 변경",
      description: `선택한 ${selectedIds.length}건을 ${label} 상태로 변경하시겠습니까?`,
      confirmLabel: "변경",
      cancelLabel: "취소",
    });

    if (!confirmed) {
      return;
    }

    setIsStatusUpdating(true);
    try {
      const response = await postData(API_ORDER_ERROR_HANDLINGS_STATUS_CRM, {
        ids: selectedIds,
        status,
        filter_site: site,
      });
      const updatedCount = Number(response?.updated_count || 0);
      toast.success(`${updatedCount}건의 상태를 ${label}로 변경했습니다.`);
      setSelectedIds([]);
      refetch(query);
    } catch (error) {
      toast.error(normalizeMessage((error as any)?.response?.data || error) || "상태 변경에 실패했습니다.");
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleErrorAction = async (action: OrderErrorAction) => {
    if (!selectedIds.length) {
      toast.error("처리할 항목을 먼저 선택해주세요.");
      return;
    }

    const actionLabel = action === "recheck" ? "재확인" : "재시도";
    const confirmed = await dialogService.confirm({
      title: `결제 오류 ${actionLabel}`,
      description:
        action === "recheck"
          ? `선택한 ${selectedIds.length}건의 PG 잔액을 다시 확인하시겠습니까?`
          : `선택한 ${selectedIds.length}건의 PG 취소를 다시 요청하시겠습니까? 이미 처리된 건은 PG 요청 없이 완료 처리됩니다.`,
      confirmLabel: actionLabel,
      cancelLabel: "취소",
    });

    if (!confirmed) {
      return;
    }

    setActiveAction(action);
    try {
      const response = await postData(API_ORDER_ERROR_HANDLINGS_ACTIONS_CRM, {
        ids: selectedIds,
        action,
        filter_site: site,
      });
      const checkedCount = Number(response?.checked_count || 0);
      const updatedCount = Number(response?.updated_count || 0);
      const failedCount = Number(response?.failed_count || 0);
      const failedMessage = failedCount ? `, 실패 ${failedCount}건` : "";
      toast.success(`${checkedCount}건 ${actionLabel} 완료: 완료표시 ${updatedCount}건${failedMessage}`);
      setSelectedIds([]);
      refetch(query);
    } catch (error) {
      toast.error(normalizeMessage((error as any)?.response?.data || error) || `${actionLabel}에 실패했습니다.`);
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="결제 오류 주문" pageTitle="관리" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="card-header border-0">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <div className="grow">
                    <h5 className="card-title mb-1">결제 오류 주문</h5>
                    <div className="text-muted small">
                      결제금액 합계 {formatCurrency(totalPaymentAmount)} · 취소 요청 금액 합계 {formatCurrency(totalCancelAmount)}
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    {(["open", "all", "resolved"] as const).map((statusKey) => (
                      <Button
                        key={statusKey}
                        color={query.status === statusKey ? "primary" : "soft-secondary"}
                        disabled={isStatusUpdating || !!activeAction}
                        onClick={() => handleStatusChange(statusKey)}
                      >
                        {STATUS_LABELS[statusKey]}
                      </Button>
                    ))}
                    <Button
                      color="soft-info"
                      disabled={isStatusUpdating || !!activeAction || selectedCount === 0}
                      onClick={() => handleErrorAction("recheck")}
                    >
                      {activeAction === "recheck" ? "재확인중..." : `재확인${selectedCount ? ` (${selectedCount})` : ""}`}
                    </Button>
                    <Button
                      color="soft-danger"
                      disabled={isStatusUpdating || !!activeAction || selectedCount === 0}
                      onClick={() => handleErrorAction("retry")}
                    >
                      {activeAction === "retry" ? "재시도중..." : `재시도${selectedCount ? ` (${selectedCount})` : ""}`}
                    </Button>
                    <Button
                      color="soft-warning"
                      disabled={isStatusUpdating || !!activeAction || selectedCount === 0}
                      onClick={() => handleBulkStatusUpdate("open")}
                    >
                      {isStatusUpdating ? "변경중..." : `미처리로 변경${selectedCount ? ` (${selectedCount})` : ""}`}
                    </Button>
                    <Button
                      color="soft-success"
                      disabled={isStatusUpdating || !!activeAction || selectedCount === 0}
                      onClick={() => handleBulkStatusUpdate("resolved")}
                    >
                      {isStatusUpdating ? "변경중..." : `처리완료로 변경${selectedCount ? ` (${selectedCount})` : ""}`}
                    </Button>
                    <Button color="success" onClick={handleExport} disabled={isExportLoading || !!activeAction}>
                      {isExportLoading ? "CSV 준비중..." : "CSV 내보내기"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="mb-3 text-muted">
                  현재 필터 기준 {count.toLocaleString()}건
                  {selectedCount ? ` · 선택 ${selectedCount}건` : ""}
                </div>
                <ServerPaginatedTable
                  id="order-error-handlings-table"
                  url={API_ORDER_ERROR_HANDLINGS_CRM}
                  columns={columns}
                  data={data}
                  previous={previous}
                  next={next}
                  count={count}
                  onRequestData={(url) => requestData(url)}
                  onChangeQuery={(param) => {
                    const merged = { ...query, ...param, filter_site: site };
                    setQuery(merged);
                  }}
                  tableQuery={{ ...query }}
                  categories={[]}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <ExportCSVModal
          show={showExportModal}
          onCloseClick={() => setShowExportModal(false)}
          data={exportRows}
          tossData={tossExportRows}
          onTossExport={handleTossExport}
        />
      </Container>
    </div>
  );
};

export default OrderErrorHandlingList;
